<template>
    <LoadingComponent :props="loading" />

    <footer class="pt-12 px-12 mobile:hidden" :style="{ backgroundColor: setting.theme_footer_bg === 'primary' ? 'rgb(var(--primary))' : 'rgb(var(--secondary))' }">
        <div class="container">
            <div class="row">
                <div class="col-12 md:col-4 lg:col-5 mb-6 md:mb-0">
                    <div class="tablet:text-center tablet:mx-auto w-full max-w-xs">
                        <router-link :to="{ name: 'frontend.home' }">
                            <img class="mb-4 w-60" :src="setting.theme_footer_logo" alt="logo" >
                        </router-link>

                        <form @submit.prevent="saveSubscription" class="mt-2 mb-10 block">
                            <label class="mb-2 ml-3 font-medium text-white text-sm">
                                {{ $t('message.subscribe_to_our_newsletter') }}
                            </label>
                            <div class="flex w-full h-10 rounded-3xl p-1 bg-white items-center">
                                <input type="email" v-model="subscriptionProps.post.email"
                                    :placeholder="$t('label.your_email_address')"
                                    class="w-full h-full pl-4 pr-2 !outline-none outline-none focus:outline-none focus-visible:outline-none !ring-0 ring-0 focus:ring-0 focus-visible:ring-0 border-0 bg-transparent text-sm text-slate-800">
                                <button type="submit"
                                    class="text-xs font-semibold flex-shrink-0 px-3 h-full rounded-3xl bg-primary text-white hover:opacity-90 transition-opacity">
                                    {{ $t('button.subscribe') }}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
                <div class="col-12 md:col-8 lg:col-7">
                    <div class="row">
                        <div class="col-6 sm:col-4 mb-4 sm:mb-0">
                            <h4 class="text-xl font-medium mb-6 text-white">{{ $t('label.help') }}</h4>
                            <router-link class="w-fit text-sm font-medium text-white mb-2 transition-all duration-300 hover:text-white-6"
                                :to="{ name: 'frontend.account.overview'}">
                                {{ $t('menu.user_account') }}
                            </router-link>
                            <nav v-if="supportPages.length > 0" class="flex flex-col gap-3 mb-4">
                                <router-link v-for="supportPage in supportPages"
                                    class="w-fit text-sm font-medium text-white transition-all duration-300 hover:text-white-6"
                                    :to="{ name: 'frontend.page', params: { slug: supportPage.slug } }">
                                    {{ supportPage.title }}
                                </router-link>
                            </nav>
                            <nav v-if="setting.social_media_facebook || setting.social_media_twitter || setting.social_media_instagram || setting.social_media_youtube"
                                class="flex flex-wrap items-center gap-3 tablet:justify-center">
                                <a v-if="setting.social_media_facebook" target="_blank" :href="setting.social_media_facebook"
                                    class="lab-fill-facebook w-7 h-7 !leading-7 text-center rounded-full text-sm text-secondary bg-white transition-all duration-300 hover:text-white hover:bg-primary"></a>
                                <a v-if="setting.social_media_twitter" target="_blank" :href="setting.social_media_twitter"
                                    class="lab-fill-x w-7 h-7 !leading-7 text-center rounded-full text-sm text-secondary bg-white transition-all duration-300 hover:text-white hover:bg-primary"></a>
                                <a v-if="setting.social_media_instagram" target="_blank" :href="setting.social_media_instagram"
                                    class="lab-fill-instagram w-7 h-7 !leading-7 text-center rounded-full text-sm text-secondary bg-white transition-all duration-300 hover:text-white hover:bg-primary"></a>
                                <a v-if="setting.social_media_youtube" target="_blank" :href="setting.social_media_youtube"
                                    class="lab-fill-youtube w-7 h-7 !leading-7 text-center rounded-full text-sm text-secondary bg-white transition-all duration-300 hover:text-white hover:bg-primary"></a>
                            </nav>
                        </div>
                        <div class="col-6 sm:col-4 mb-4 sm:mb-0">
                            <h4 class="text-xl font-medium mb-6 text-white">{{ $t('label.legal') }}</h4>
                            <nav v-if="legalPages.length > 0" class="flex flex-col gap-3">
                                <router-link v-for="legalPage in legalPages"
                                    class="w-fit text-sm font-medium text-white transition-all duration-300 hover:text-white-6"
                                    :to="{ name: 'frontend.page', params: { slug: legalPage.slug } }">
                                    {{ legalPage.title }}
                                </router-link>
                            </nav>
                        </div>
                        <div class="col-12 sm:col-4">
                            <h4 class="text-xl font-medium mb-6 text-white">
                                {{ $t('label.contact') }}</h4>
                            <ul class="flex flex-col gap-4">
                                <li class="flex gap-3">
                                    <i class="lab-fill-location text-sm flex-shrink-0 text-white"></i>
                                    <span class="text-sm font-medium text-white">{{ setting.company_address }}</span>
                                </li>
                                <li class="flex gap-3">
                                    <i class="lab-fill-mail text-sm flex-shrink-0 text-white"></i>
                                    <span class="text-sm font-medium text-white">{{ setting.company_email }}</span>
                                </li>
                                <li class="flex gap-3">
                                    <i class="lab-fill-calling text-sm flex-shrink-0 text-white"></i>
                                    <span class="text-sm font-medium text-white">[{{ setting.company_calling_code }}] {{ setting.company_phone }}</span>
                                </li>
                            </ul>

                            <dl class="mt-6">
                                <dd class="flex gap-3">
                                    <a target="_blank" class="router-link-active router-link-exact-active"
                                        v-if="setting.site_android_app_link" :href="setting.site_android_app_link">
                                        <img class="h-8 rounded-lg" :src="setting.image_play_store" alt="app"
                                            >
                                    </a>
                                    <a target="_blank" class="router-link-active router-link-exact-active"
                                        v-if="setting.site_ios_app_link" :href="setting.site_ios_app_link">
                                        <img class="h-8 rounded-lg" :src="setting.image_app_store" alt="app">
                                    </a>
                                </dd>
                            </dl>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="pt-4 pb-24 lg:py-4 mt-8 text-center border-footer">
            <p class="text-xs font-medium text-white">{{ setting.site_copyright }}</p>
        </div>
    </footer>

    <!-- Subscriber Name Personalization Modal Start -->
    <Teleport to="body">
        <div v-if="isNameModalOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-light/50 backdrop-blur-sm transition-opacity">
            <div class="bg-white rounded-xl shadow-lg max-w-md w-full p-6 sm:p-8 transform transition-all text-slate-800 relative min-h-[220px] flex flex-col justify-center">
                
                <!-- Loading state: ONLY centered bar-fade-scale spinner -->
                <div v-if="isSubscribingEmail" class="w-full h-20 flex items-center justify-center relative">
                    <VueElementLoading spinner="bar-fade-scale" :color="spinnerColor" :active="true" :is-full-screen="false" />
                </div>

                <!-- Success State: Revealed after name submission resolves -->
                <div v-else-if="isSuccessState" class="text-center py-4">
                    <button type="button" @click="closeNameModal" class="absolute top-4 right-4 text-slate-400 hover:text-slate-600 text-xl font-bold p-1 leading-none">
                        &times;
                    </button>
                    <div class="w-16 h-16 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl shadow-inner">
                        🎉
                    </div>
                    <h3 class="text-xl font-bold text-slate-900 mb-3">{{ $t('message.subscriber_name_success_title') }}</h3>
                    <p class="text-sm text-slate-600 leading-relaxed mb-6 px-2">
                        {{ $t('message.subscriber_name_success_text') }}
                    </p>
                    <div class="flex justify-center w-full">
                        <button type="button" @click="closeNameModal" class="px-8 py-3 rounded-xl text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 hover:opacity-80 transition-colors">
                            {{ $t('button.close') }}
                        </button>
                    </div>
                </div>

                <!-- Form state: Revealed after email subscription resolves -->
                <div v-else class="w-full min-h-[220px]">
                    <button type="button" @click="closeNameModal" class="absolute top-4 right-4 text-slate-400 hover:text-slate-600 text-xl font-bold p-1 leading-none">
                        &times;
                    </button>

                    <div class="text-center mb-6">
                        <div class="w-14 h-14 bg-blue-50 text-primary rounded-full flex items-center justify-center mx-auto mb-4 text-2xl shadow-inner">
                            ✨
                        </div>
                        <h3 class="text-xl font-bold text-slate-900 mb-2">{{ $t('message.subscriber_name_title') }}</h3>
                        <p class="text-sm text-slate-600 leading-relaxed">
                            {{ $t('message.subscriber_name_subtitle') }}
                        </p>
                    </div>

                    <form @submit.prevent="saveSubscriberName" class="w-full space-y-4">
                        <div class="w-full flex flex-col items-center">
                            <label class="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">{{ $t('message.subscriber_name_label') }}</label>
                            <input type="text" v-model="subscriberName" :placeholder="$t('message.subscriber_name_placeholder')" 
                                class="w-full px-4 py-3 text-center rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 text-sm outline-none transition-all" />
                        </div>

                        <div class="flex items-center gap-3 pt-2 px-2 mt-3 mb-1 w-full">
                            <button type="button" @click="closeNameModal" class="flex-1 py-3 px-4 rounded-xl text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 hover:opacity-80 transition-colors">
                                {{ $t('button.skip') }}
                            </button>
                            <button type="submit" :disabled="!subscriberName || !subscriberName.trim() || isSubmittingName" class="flex-1 py-3 px-4 rounded-xl text-sm font-semibold text-white bg-primary hover:opacity-80 transition-opacity disabled:opacity-50 flex items-center justify-center gap-1">
                                <template v-if="isSubmittingName">
                                    <svg class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span>{{ $t('button.sending') }}</span>
                                </template>
                                <template v-else>
                                    <span>{{ $t('button.send') }}</span>
                                </template>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </Teleport>
    <!-- Subscriber Name Personalization Modal End -->
</template>


<script>
import statusEnum from "../../../enums/modules/statusEnum";
import axios from "axios";
import alertService from "../../../services/alertService";
import LoadingComponent from "../../frontend/components/LoadingComponent";
import VueElementLoading from 'vue-element-loading';
import ENV from "../../../config/env";
import menuSectionEnum from "../../../enums/modules/menuSectionEnum";
import _ from "lodash";

export default {
    name: "FrontendFooterComponent",
    components: { LoadingComponent, VueElementLoading },
    data() {
        return {
            loading: {
                isActive: false,
            },
            legalPages: [],
            supportPages: [],
            enums: {
                statusEnum: statusEnum,
                menuSectionEnum: menuSectionEnum
            },
            subscriptionProps: {
                post: {
                    email: ""
                }
            },
            isNameModalOpen: false,
            isSubscribingEmail: false,
            isSubmittingName: false,
            isSuccessState: false,
            subscriberId: null,
            subscriberName: ""
        }
    },
    computed: {
        setting: function () {
            return this.$store.getters['frontendSetting/lists'];
        },
        spinnerColor: function () {
            return ENV.LOADER_COLOR;
        }
    },
    mounted() {
        this.loading.isActive = true;
        this.$store.dispatch("frontendPage/lists", {
            paginate: 0,
            order_column: "id",
            order_type: "asc",
            status: this.enums.statusEnum.ACTIVE
        }).then(res => {
            if (res.data.data.length > 0) {
                _.forEach(res.data.data, (page) => {
                    if (page.menu_section_id === this.enums.menuSectionEnum.LEGAL) {
                        this.legalPages.push(page);
                    } else {
                        this.supportPages.push(page);
                    }
                });
            }
            this.loading.isActive = false;
        }).catch((err) => {
            this.loading.isActive = false;
        });
    },
    unmounted() {
        document.body.classList.remove('overflow-hidden');
    },
    methods: {
        saveSubscription: function () {
            const email = this.subscriptionProps.post.email ? this.subscriptionProps.post.email.trim() : "";
            if (!email) return;

            // Lock body scroll while modal is open
            document.body.classList.add('overflow-hidden');

            this.isNameModalOpen = true;
            this.isSubscribingEmail = true;
            this.isSuccessState = false;
            this.subscriberId = null;

            const url = '/frontend/subscriber';
            axios.post(url, { email: email }).then(res => {
                this.isSubscribingEmail = false;
                this.subscriptionProps.post.email = "";
                alertService.success(this.$t("message.subscribe"));
                if (res.data && res.data.data && res.data.data.id) {
                    this.subscriberId = res.data.data.id;
                    this.subscriberName = "";
                } else {
                    this.closeNameModal();
                }
            }).catch((err) => {
                this.isSubscribingEmail = false;
                this.closeNameModal();
                if (err.response && err.response.data && err.response.data.message) {
                    alertService.error(err.response.data.message);
                } else {
                    alertService.error("Error al procesar la suscripción.");
                }
            });
        },
        saveSubscriberName: function () {
            if (!this.subscriberId) {
                this.closeNameModal();
                return;
            }
            if (!this.subscriberName || !this.subscriberName.trim()) {
                return;
            }
            this.isSubmittingName = true;
            axios.post(`/frontend/subscriber/${this.subscriberId}/name`, {
                name: this.subscriberName.trim()
            }).then(res => {
                this.isSubmittingName = false;
                this.isSuccessState = true;
                alertService.success(this.$t("message.subscriber_name_updated"));
            }).catch(err => {
                this.isSubmittingName = false;
                this.closeNameModal();
            });
        },
        closeNameModal: function () {
            document.body.classList.remove('overflow-hidden');
            this.isNameModalOpen = false;
            this.isSubscribingEmail = false;
            this.isSuccessState = false;
            this.subscriberId = null;
            this.subscriberName = "";
            this.isSubmittingName = false;
        }
    }
}
</script>
